import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Label,
  Input,
  InputGroup,
  InputGroupText,
  Table,
  Card,
  CardHeader,
  CardBody,
} from "reactstrap";
import { CSVLink } from "react-csv";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import PaginatedItems from "../../components/PaginatedItems";
import moment from "moment";
import { formatCurrencyValue, showToast } from "../../helper-methods";
import SwapDetailsModal from "../../components/modals/SwapDetailsModal";
import { Oval } from "react-loader-spinner";
import { fetchAllSwaps } from "../../http/http-calls";

// Helper to map backend swap type to display label
const formatSwapType = (type) => {
  const typeMap = {
    gated: "Email Capture",
    paywall: "Paywall",
    vote: "Fan Vote",
  };
  return typeMap[type] || type;
};

// Helper to map backend status to display label
const formatStatus = (status) => {
  return status.charAt(0).toUpperCase() + status.slice(1);
};

const SwapsPage = () => {
  // Refs
  const csvLinkRef = useRef();
  const debounceRef = useRef();

  // States
  const [allSwaps, setAllSwaps] = useState([]);
  const [swaps, setSwaps] = useState([]);
  const [count, setCount] = useState(0);
  const [itemPerPage, setItemPerPage] = useState(10);
  const [payload, setPayload] = useState({
    pageNumber: 1,
    pageSize: 10,
    sortBy: "Created At",
    sortOrder: "high-to-low",
  });

  // Stats states
  const [totalSwaps, setTotalSwaps] = useState(0);
  const [activeSwaps, setActiveSwaps] = useState(0);
  const [totalSignups, setTotalSignups] = useState(0);
  const [totalRevenue, setTotalRevenue] = useState(0);

  // Modal and loading states
  const [selectedSwap, setSelectedSwap] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [csvLoading, setCsvLoading] = useState(false);
  const [isEmbeddedLoading, setIsEmbeddedLoading] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  const fetchSwaps = useCallback(
    async (pageConfig = payload) => {
      try {
        if (
          !pageConfig.isFetchAll &&
          !pageConfig.isInitialLoad &&
          !isInitialLoad
        ) {
          setIsEmbeddedLoading(true);
        }

        // Build API payload - backend expects lowercase status/type values
        const apiPayload = {
          pageNumber: pageConfig.pageNumber,
          pageSize: pageConfig.isFetchAll ? 1000 : pageConfig.pageSize, // Fetch all for CSV
          sortBy: "createdAt",
          sortOrder: pageConfig.sortOrder,
        };

        // Add filters if present
        if (pageConfig.status) {
          apiPayload.status = pageConfig.status;
        }
        if (pageConfig.type) {
          apiPayload.type = pageConfig.type;
        }
        if (pageConfig.searchText) {
          apiPayload.searchText = pageConfig.searchText;
        }

        const response = await fetchAllSwaps(apiPayload);

        // Update stats from response
        setTotalSwaps(response.totalSwaps || 0);
        setActiveSwaps(response.activeSwaps || 0);
        setTotalSignups(response.totalSignups || 0);
        setTotalRevenue(response.totalRevenue || 0);

        if (!pageConfig.isFetchAll) {
          // Regular pagination - set table data
          setSwaps(response.data || []);
          setCount(response.swapCount || 0);
          setItemPerPage((response.data || []).length);
        } else {
          // CSV export - format for CSV
          const csvData = (response.data || []).map((swap) => ({
            "Artist Name": swap.artist?.name || "Unknown",
            "Artist Email": swap.artist?.email || "",
            "Swap Title": swap.title || "",
            Signups: swap.participantsCount != null ? swap.participantsCount : "—",
            Type: formatSwapType(swap.type),
            Status: formatStatus(swap.status),
            Created: moment(swap.createdAt).format("MMM DD, YYYY"),
          }));
          setAllSwaps(csvData);
        }

        if (
          !pageConfig.isFetchAll &&
          !pageConfig.isInitialLoad &&
          !isInitialLoad
        ) {
          setIsEmbeddedLoading(false);
        }
        if (isInitialLoad || pageConfig.isInitialLoad) setIsInitialLoad(false);
      } catch (error) {
        if (
          !pageConfig.isFetchAll &&
          !pageConfig.isInitialLoad &&
          !isInitialLoad
        ) {
          setIsEmbeddedLoading(false);
        }
        if (isInitialLoad || pageConfig.isInitialLoad) setIsInitialLoad(false);
        showToast(error?.reason || error?.message || "Error fetching swaps", "error");
      }
    },
    [payload, isInitialLoad],
  );

  const fetchAllSwapsForCSV = useCallback(
    async (filterConfig = payload) => {
      await fetchSwaps({ ...filterConfig, isFetchAll: true });
    },
    [fetchSwaps, payload],
  );

  // CSV export handler
  const handleCSVExport = async (e) => {
    e.preventDefault();

    setCsvLoading(true);
    await fetchAllSwapsForCSV(payload);
    setCsvLoading(false);

    setTimeout(() => {
      if (csvLinkRef.current) {
        csvLinkRef.current.link.click();
      }
    }, 0);
  };

  // Fetch swaps on component mount
  useEffect(() => {
    fetchSwaps({ ...payload, isInitialLoad: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Refetch when payload changes (but not on initial load)
  useEffect(() => {
    if (!isInitialLoad) {
      fetchSwaps(payload);
    }
  }, [payload, isInitialLoad, fetchSwaps]);

  // Filter and search
  const statusHandler = useCallback(
    async (field, event, value) => {
      const newSearchData = { ...payload };
      const fieldValue = event?.target?.value || value;

      if (
        fieldValue !== null &&
        fieldValue !== undefined &&
        fieldValue !== ""
      ) {
        newSearchData[field] = fieldValue;
        newSearchData.pageNumber = 1;
        setPayload(newSearchData);
      } else {
        delete newSearchData[field];
        setPayload({ ...newSearchData, pageNumber: 1 });
      }
    },
    [payload],
  );

  // Debouncing function call
  const optimizedFn = useCallback(
    (...args) => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        statusHandler(...args);
      }, 500);
    },
    [statusHandler],
  );

  // Pagination
  const handlePageClick = (pageNumber) => {
    let newPageConfig = { ...payload, pageNumber: pageNumber + 1 };
    setPayload(newPageConfig);
  };

  // Toggle modal
  const toggleModal = (swap = null) => {
    setSelectedSwap(swap);
    setIsModalOpen(!isModalOpen);
  };

  return (
    <>
      <div className="innerHeader">
        <h2>Swaps</h2>
      </div>

      {/* Stats Cards */}
      <div className="dashboardCardWrap">
        <Card className="dashboardCard oneQuarter">
          <CardHeader>
            Total
            <br />
            Swaps
            <span className="bgIcon">
              <i className="fa fa-exchange-alt" />
            </span>
          </CardHeader>
          <CardBody>
            <p>{totalSwaps}</p>
          </CardBody>
        </Card>

        <Card className="dashboardCard oneQuarter">
          <CardHeader>
            Active
            <br />
            Swaps
            <span className="bgIcon">
              <i className="fa fa-check-circle" />
            </span>
          </CardHeader>
          <CardBody>
            <p>{activeSwaps}</p>
          </CardBody>
        </Card>

        <Card className="dashboardCard oneQuarter">
          <CardHeader>
            Total
            <br />
            Signups
            <span className="bgIcon">
              <i className="fa fa-users" />
            </span>
          </CardHeader>
          <CardBody>
            <p>{totalSignups.toLocaleString()}</p>
          </CardBody>
        </Card>

        <Card className="dashboardCard oneQuarter">
          <CardHeader>
            Total
            <br />
            Revenue
            <span className="bgIcon">
              <i className="fas fa-dollar-sign" />
            </span>
          </CardHeader>
          <CardBody>
            <p>{formatCurrencyValue(totalRevenue)}</p>
          </CardBody>
        </Card>
      </div>

      {/* Filters */}
      <div className="filterWrapper mt-2">
        <div className="filterIcon">
          <i className="fas fa-filter" />
        </div>

        <div className="filterForm">
          <div className="formGroup">
            <Label>Status</Label>
            <Input
              type="select"
              onChange={(event) => optimizedFn("status", event)}
            >
              <option value="">All Status</option>
              <option value="published">Active</option>
              <option value="completed">Completed</option>
              <option value="archived">Archived</option>
            </Input>
          </div>

          <div className="formGroup">
            <Label>Type</Label>
            <Input
              type="select"
              onChange={(event) => optimizedFn("type", event)}
            >
              <option value="">All Types</option>
              <option value="gated">Email Capture</option>
              <option value="paywall">Paywall</option>
              <option value="vote">Fan Vote</option>
            </Input>
          </div>

          <div className="formGroup">
            <Label>Export</Label>
            <div className="rounded-xl overflow-hidden bg-black py-2 px-4">
              <button
                onClick={handleCSVExport}
                className="text-white text-decoration-none border-0 bg-transparent"
                disabled={csvLoading}
              >
                <i className="fas fa-download me-2" />
                {csvLoading ? "Preparing..." : "Download"}
              </button>
            </div>
            <CSVLink
              data={allSwaps}
              filename={`swaps-${moment().format("YYYY-MM-DD")}.csv`}
              className="hidden"
              ref={csvLinkRef}
              target="_blank"
            />
          </div>

          {/* search */}
          <div className="formGroup searchbar">
            <Label>Search</Label>
            <InputGroup>
              <Input
                placeholder="Search..."
                onChange={(event) => optimizedFn("searchText", event)}
              />
              <InputGroupText>
                <i className="fas fa-search" />
              </InputGroupText>
            </InputGroup>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="tableSection mt-3 bg-white p-3">
        {isEmbeddedLoading ? (
          <div
            className="d-flex justify-content-center align-items-center"
            style={{ height: "400px" }}
          >
            <Oval height="50" width="50" color="#3498DB" />
          </div>
        ) : (
          <>
            <Table responsive hover>
              <thead>
                <tr>
                  <th>Artist</th>
                  <th>Swap Title</th>
                  <th>Signups</th>
                  <th>Type</th>
                  <th>Status</th>
                  <th>Created</th>
                </tr>
              </thead>
              <tbody>
                {swaps.map((swap) => (
                  <tr
                    key={swap.id}
                    onClick={() => toggleModal(swap)}
                    className="cursor-pointer"
                  >
                    <td>
                      <div>
                        <div className="fw-sm-bold text-primary">
                          {swap.artist?.name || "Unknown"}
                        </div>
                        <div className="mt-1 text-muted fs-14">
                          {swap.artist?.email || "—"}
                        </div>
                      </div>
                    </td>
                    <td>{swap.title}</td>
                    <td>
                      {swap.participantsCount != null
                        ? swap.participantsCount.toLocaleString()
                        : "—"}
                    </td>
                    <td>{formatSwapType(swap.type)}</td>
                    <td>
                      <span
                        className={`badge ${
                          swap.status === "published"
                            ? "bg-success"
                            : swap.status === "completed"
                              ? "bg-secondary"
                              : "bg-dark"
                        }`}
                      >
                        {formatStatus(swap.status)}
                      </span>
                    </td>
                    <td>{moment(swap.createdAt).format("MMM DD, YYYY")}</td>
                  </tr>
                ))}
              </tbody>
            </Table>

            {swaps.length === 0 && (
              <div className="text-center py-5">
                <p className="text-muted">No swaps found</p>
              </div>
            )}
          </>
        )}
      </div>

      {/* Pagination */}
      {count > 0 && (
        <PaginatedItems
          itemsPerPages={itemPerPage}
          totalCount={count}
          selectedPage={payload.pageNumber}
          itemsPerPage={payload.pageSize}
          onPageChange={handlePageClick}
        />
      )}

      {/* Swap Details Modal */}
      <SwapDetailsModal
        isOpen={isModalOpen}
        toggle={() => toggleModal()}
        swapData={selectedSwap}
      />

      <ToastContainer />
    </>
  );
};

export default SwapsPage;
